// Reusable page-level components (skeleton, empty, page header)
import React from 'react';
import { Button } from './ui/button';
import { Plus } from 'lucide-react';

export function PageHeader({ title, subtitle, action, actionLabel = 'Novo', onAction, actionIcon: Icon = Plus }) {
  return (
    <div className="flex items-start justify-between gap-4 mb-6">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight">{title}</h1>
        {subtitle && <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">{subtitle}</p>}
      </div>
      {action ? (
        action
      ) : onAction ? (
        <Button onClick={onAction} className="bg-brand hover:bg-[#A81E23] text-white">
          <Icon className="h-4 w-4 mr-2" /> {actionLabel}
        </Button>
      ) : null}
    </div>
  );
}

export function TableSkeleton({ rows = 5, cols = 5 }) {
  return (
    <div className="space-y-2">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="flex gap-3">
          {Array.from({ length: cols }).map((_, j) => (
            <div key={j} className="h-10 flex-1 rounded-md shimmer" />
          ))}
        </div>
      ))}
    </div>
  );
}

export function EmptyState({ icon: Icon, title, description, action }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      {Icon && <Icon className="h-12 w-12 text-neutral-300 dark:text-neutral-700 mb-3" />}
      <h3 className="font-semibold text-neutral-700 dark:text-neutral-300">{title}</h3>
      {description && <p className="text-sm text-neutral-500 mt-1 max-w-sm">{description}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function StatusBadge({ status, variant = 'auto' }) {
  let cls = 'bg-neutral-200 text-neutral-700';
  const s = (status || '').toLowerCase();
  if (['active', 'ativo'].includes(s)) cls = 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300';
  else if (['inactive', 'inativo'].includes(s)) cls = 'bg-neutral-200 text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300';
  else if (['manutenção', 'manutencao'].includes(s)) cls = 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300';
  else if (['baixado'].includes(s)) cls = 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${cls}`}>
      {status}
    </span>
  );
}

export function Pagination({ page, total, limit, onPageChange }) {
  const totalPages = Math.max(1, Math.ceil(total / limit));
  if (totalPages <= 1) return null;
  return (
    <div className="flex items-center justify-between px-2 py-3 text-sm">
      <span className="text-neutral-500">
        Mostrando {(page - 1) * limit + 1}–{Math.min(page * limit, total)} de {total}
      </span>
      <div className="flex gap-1">
        <Button size="sm" variant="outline" onClick={() => onPageChange(page - 1)} disabled={page <= 1}>
          Anterior
        </Button>
        <div className="px-3 py-1 rounded-md bg-neutral-100 dark:bg-neutral-800 text-sm">
          {page} / {totalPages}
        </div>
        <Button size="sm" variant="outline" onClick={() => onPageChange(page + 1)} disabled={page >= totalPages}>
          Próxima
        </Button>
      </div>
    </div>
  );
}
